import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ProgressBar, StarBadge } from "@/components/Bits";
import { AvatarImg } from "@/components/Profile";
import { CATEGORIES, GAMES, IMG } from "@/lib/content";
import { BADGES } from "@/lib/badges";
import { getState, subscribe, ensureDailyAdventure, sectionMastery } from "@/lib/progress";
import { ArrowRight, Play } from "lucide-react";

const tileBg = { yellow: "bg-amber-100 border-amber-200", blue: "bg-sky-100 border-sky-200", green: "bg-emerald-100 border-emerald-200", pink: "bg-pink-100 border-pink-200", purple: "bg-violet-100 border-violet-200", orange: "bg-orange-100 border-orange-200", sky: "bg-cyan-100 border-cyan-200" };

export default function Home() {
  const [state, setState] = useState(getState());
  useEffect(() => { ensureDailyAdventure(); setState(getState()); return subscribe(setState); }, []);

  const daily = state.dailyAdventure?.tasks ? state.dailyAdventure : { tasks: [] };
  const doneCount = daily.tasks.filter(t => t.done).length;
  const dailyPct = daily.tasks.length ? Math.round((doneCount / daily.tasks.length) * 100) : 0;
  const name = state.profile?.name || "friend";

  return (
    <div className="ll-bg min-h-screen pb-28 md:pb-8">
      <Sparkles />
      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8">
        <section className="ll-card overflow-hidden relative mt-3 md:mt-6" data-testid="hero-section">
          <div className="absolute inset-0 -z-0" style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 40%, #C4B5FD 100%)" }} />
          <div className="relative grid md:grid-cols-2 gap-6 md:gap-4 p-6 sm:p-10 md:p-14">
            <div className="text-white z-10">
              <div className="inline-flex items-center gap-3 bg-white/25 backdrop-blur pl-1.5 pr-4 py-1.5 rounded-full" data-testid="greeting">
                <AvatarImg avatar={state.profile?.avatar} className="w-9 h-9 rounded-full bg-white/70" />
                <span className="font-display font-bold text-base">Hi, {name}! 👋</span>
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">Learn <span className="text-yellow-300">with fun</span>,<br />grow every day!</h1>
              <p className="mt-4 text-white/85 text-lg max-w-md">A playful little learning world where curious minds discover letters, numbers, animals, shapes and colors.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link data-testid="start-learning-button" to="/learn" className="ll-btn ll-btn-primary text-lg">Start Learning <ArrowRight size={20} /></Link>
                <Link data-testid="explore-activities-button" to="/play" className="ll-btn ll-btn-secondary text-lg"><Play size={18} /> Play Games</Link>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <StarBadge count={state.stars} testId="hero-stars" />
                <div className="text-white/85 text-sm font-semibold" data-testid="hero-streak">🔥 {state.streak} day streak</div>
              </div>
            </div>
            <div className="relative min-h-[280px] md:min-h-[380px] flex items-center justify-center">
              <img src={state.profile?.avatar === "girl" ? IMG.hero_girl : IMG.hero_kids} alt="Kids learning" className="w-full max-w-[440px] drop-shadow-[0_25px_35px_rgba(0,0,0,0.25)] ll-float" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div className="absolute top-2 right-6 text-4xl ll-float-slow">⭐</div>
              <div className="absolute bottom-6 left-2 text-3xl ll-float">🌈</div>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-4 mt-6" data-testid="daily-adventure">
          <div className="ll-card p-5 md:col-span-2 relative overflow-hidden bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs uppercase font-bold tracking-widest text-yellow-300">Today's Adventure</div>
                <div className="font-display font-bold text-2xl sm:text-3xl mt-1">{daily.completed ? "Adventure complete! 🎉" : `${daily.tasks.length} tasks today · earn 10 ⭐`}</div>
                <div className="mt-3 max-w-md"><ProgressBar percent={dailyPct} testId="daily-progress" /></div>
                <div className="text-sm mt-2 text-white/85" data-testid="daily-count">{doneCount} of {daily.tasks.length} completed</div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                  {daily.tasks.map(t => (
                    <li key={t.id}>
                      <Link to={t.path} data-testid={`daily-task-${t.id.replace(":", "-")}`} className={`flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold transition ${t.done ? "bg-emerald-400/30 line-through text-white/70" : "bg-white/15 hover:bg-white/25"}`}>
                        <span className="text-lg">{t.done ? "✅" : t.emoji}</span><span className="truncate">{t.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <img src={IMG.trophy} alt="Trophy" className="w-20 sm:w-28 ll-float hidden sm:block" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          </div>
          <div className="grid gap-4">
            <div className="ll-card p-5 flex items-center gap-4 bg-yellow-50 border-yellow-200">
              <img src={IMG.star} alt="Star" className="w-16 ll-float" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div>
                <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Your Stars</div>
                <div className="font-display text-4xl font-bold text-purple-900">{state.stars}</div>
                <Link data-testid="see-progress-link" to="/progress" className="text-purple-600 text-sm font-bold underline">See progress</Link>
              </div>
            </div>
            <Link to="/progress" data-testid="badges-card" className="ll-card p-4 flex items-center gap-3 bg-white hover:-translate-y-1 transition">
              <div className="flex -space-x-2">
                {(state.badges || []).slice(0, 4).map(id => <span key={id} className="w-10 h-10 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center text-xl">{BADGES.find(b => b.id === id)?.emoji}</span>)}
                {!(state.badges || []).length && <span className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-xl">🏅</span>}
              </div>
              <div className="text-sm font-bold text-purple-900">{(state.badges || []).length} / {BADGES.length} badges</div>
            </Link>
          </div>
        </section>

        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-purple-500 uppercase tracking-widest text-xs font-bold">Explore & Discover</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-purple-900 mt-1">What shall we learn?</h2>
            </div>
            <Link to="/learn" data-testid="all-sections-link" className="hidden sm:inline text-purple-600 font-bold">All →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5">
            {CATEGORIES.map((c) => (
              <Link key={c.id} to={c.path} data-testid={`activity-card-${c.id}`} className={`ll-tile ${tileBg[c.color] || tileBg.purple}`}>
                <div className="aspect-square rounded-2xl bg-white/70 flex items-center justify-center overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-4/5 h-4/5 object-contain ll-float-slow" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: c.emoji, className: "text-7xl" })); }} />
                </div>
                <div className="mt-3">
                  <div className="font-display font-bold text-lg text-purple-900">{c.short}</div>
                  <div className="text-purple-700/80 text-xs font-semibold">{c.modes.length} activities</div>
                  <div className="mt-2"><ProgressBar percent={sectionMastery(c.id)} /></div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h3 className="font-display font-bold text-2xl text-purple-900">Mini-games</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {GAMES.slice(0, 4).map((g) => (
              <Link key={g.id} to={g.path} data-testid={`game-card-${g.id}`} className={`ll-tile ${tileBg[g.color] || tileBg.purple} flex items-center gap-3`}>
                <img src={g.image} alt="" className="w-14 h-14 object-contain" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("div"), { textContent: g.emoji, className: "text-4xl" })); }} />
                <div>
                  <div className="font-display font-bold text-base text-purple-900">{g.title}</div>
                  <div className="text-purple-700/80 text-xs font-semibold">{g.desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
